import { useSnackbar } from 'notistack'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import EditIcon from '@mui/icons-material/Edit'
import LockResetIcon from '@mui/icons-material/LockReset'
import DeleteIcon from '@mui/icons-material/Delete';
import { groupBy } from 'lodash'
import { Fragment, useContext, useEffect, useRef, useState } from 'react'
import {
	Box,
	Button,
	IconButton,
	Typography,
	Tooltip,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
} from '@mui/material'

import { useMeta } from '@contexts/Meta'
import { useDialog } from '@hooks/useDialog'
import useAPIFetch from '@hooks/useAPIFetch'
import { RecipientDetailInfo } from '@models/recipient.model'
import useIsMounted from '@hooks/useIsMounted'
import { OptionsContext } from '@contexts/Options'
import RecipientService from '@services/users/recipient.service'
import AuthService from '@services/users/auth.service'
import { TableView } from '@components/customized/datatable'
import { sortByName } from '@utils/sortByName'
import { AuthContext } from '@contexts/Auth'
import { PermissionCodeEnum } from '@models/permission'
import { hasPermission } from '@utils/permission_helper'
import CommonService from '@services/common/common.service'
import * as XLSX from 'xlsx-js-style'

import { DatePicker } from '@mui/x-date-pickers';
import moment from 'moment'
import { Filters } from '@models/common.model'

export default function SmsRecipientsTable() {
	const { t } = useTranslation()
	const navigate = useNavigate()
	const { enqueueSnackbar } = useSnackbar()
	const [resetPwUser, setresetPwUser] = useState<string>('')
	const { state, dispatch } = useContext(OptionsContext)
	const isMounted = useIsMounted()
	const [, { setMeta }] = useMeta()
	const { setRequest, isLoading } = useAPIFetch()
	const { setRequest: setExportRequest, isLoading: isExportLoading } = useAPIFetch()
	const { setRequest: setExportAuditRequest, isLoading: isExportAuditLoading } = useAPIFetch()
	const refMounted = useRef(true)
	const [filterOptions, setFilterOptions] = useState<any>({})
	const { state: authState } = useContext(AuthContext)
	const [openEexportAudit, setOpenEexportAudit] = useState(false)
	const [dateRange, setDateRange] = useState({
		startDate: moment().startOf('month').toDate(),
		endDate: moment().endOf('month').toDate(),
	})
	const [recipientToDelete, setRecipientToDelete] = useState<RecipientDetailInfo | null>(null)

	useEffect(() => {
		setMeta({ isLoading })
	}, [isLoading])

	const exportSmsRecipients = async () => {}

	const deleteSmsRecipient = async (recipient: RecipientDetailInfo) => {
		if (!recipient.contact_id) {
			enqueueSnackbar('Invalid recipient data', { variant: 'error' })
			return
		}

		setRequest({
			callback: async () => {
				try {
				
					if (recipient.groups && recipient.groups.length > 0) {
						for (const group of recipient.groups) {
							if (group.group_id) {
								console.log('Removing contact from group:', group.building, group.group_id)
								await RecipientService.RemoveContactFromGroup(group.group_id, recipient.contact_id)
							}
						}
					}

					// Step 2: Delete contact info(s)
					if (recipient.contact_infos && recipient.contact_infos.length > 0) {
						for (const contactInfo of recipient.contact_infos) {
							if (contactInfo.contact_info_id) {
								console.log('Deleting contact info:', contactInfo.contact_info_id)
								await RecipientService.DeleteContactInfo(contactInfo.contact_info_id)
							}
						}
					}


					
					await RecipientService.DeleteContact(recipient.contact_id)

					enqueueSnackbar('SMS Recipient deleted successfully', { variant: 'success' })
			
					refMounted.current = false
					setTimeout(() => {
						refMounted.current = true
					}, 100)

					navigate('.', { replace: true });
					
				} catch (error: any) {
					console.error('Delete error:', error)
					enqueueSnackbar(error?.response?.data?.message || 'Failed to delete recipient', {
						variant: 'error',
					})
				}
			},
		})
	}

	const defaultFilterCriteria: Filters = {
		// active: { operator: '=', value: false }
	}
	return (
		<>
			<TableView
				columns={[
					{
						key: 'name',
						desc: t('Name'),
						width: 180,
						renderCell: (params: RecipientDetailInfo) => {
							return (
								<Fragment>
									<Tooltip title={params?.name}>
										<Box sx={{ 'white-space': 'break-spaces' }}>
										{params?.name}
										</Box>
									</Tooltip>
								</Fragment>
							)
						},
					},
					{
						key: 'contact_infos.info',
						desc: t('Phone'),
						width: 150,
						renderCell: (params: RecipientDetailInfo) => {
							return (
								<Fragment>
									<Tooltip title={params?.contact_infos?.[0]?.info}>
										<Box sx={{ 'white-space': 'break-spaces' }}>
											{params?.contact_infos?.[0]?.info?.length > 15
												? params?.contact_infos?.[0]?.info.substring(0, 15) + '...'
												: params?.contact_infos?.[0]?.info}
										</Box>
									</Tooltip>
								</Fragment>
							)
						},
					},
					{
						key: 'buildings',
						desc: t('SMS Group'),
						width: 200,
						renderCell: (params: RecipientDetailInfo) => {
							return (
								<Fragment>
									<Tooltip title={params?.groups.map(group => group.building).join(',')}>
										<Box sx={{ 'white-space': 'break-spaces' }}>
											{params?.groups.map(group => group.building).join(',')}
										</Box>
									</Tooltip>
								</Fragment>
							)
						},
					},
					{
						key: 'Position',
						desc: t('Position'),
						width: 200,
						renderCell: (params: RecipientDetailInfo) => {
							return (
								<Fragment>
									<Tooltip title={params?.position}>
										<Box sx={{ 'white-space': 'break-spaces' }}>
											{params?.position}
										</Box>
									</Tooltip>
								</Fragment>
							)
						}
					},
					{
						key: 'id',
						desc: t('Function Buttons'),
						width: 150,
						renderCell: (params: RecipientDetailInfo) => {
							return (
								<Box sx={{ 'white-space': 'break-spaces' }}>
									{(
										<IconButton
											onClick={() =>
												navigate(`/configurations/users_config/sms_recipients/edit/${params?.contact_id}`)
											}
										>
											<Tooltip title={'Edit User'}>
												<EditIcon>{t('Edit')}</EditIcon>
											</Tooltip>
										</IconButton>
									)}

									{(
										<IconButton
											onClick={() => setRecipientToDelete(params)}
										>
											<Tooltip title={'Delete Recipient'}>
												<DeleteIcon>{t('Delete')}</DeleteIcon>
											</Tooltip>
										</IconButton>
									)}
								</Box>
							)
						},
						disableSort: true,
					},
					
				]}
				reloadCallback={async (req) => {
					const { pagination, search, sort,...rest } = req
					
					if (refMounted.current && isMounted()) {
						let resp = await RecipientService.GetRecipientSummary(req)
						return {data: resp.items, total_count: resp.total_count}
					}
				}}
				toolbar={true}
				mountedRef={refMounted}
				extraButton={
					<>
						{hasPermission(authState?.userInfo, [PermissionCodeEnum.UserCreate]) ? (
							<Button
								variant="contained"
								color="defaultButton"
								onClick={() => navigate('/configurations/users_config/sms_recipients/create')}
							>
								{t('Create Recipient')}
							</Button>
						) : undefined}
						{/* {hasPermission(authState?.userInfo, [PermissionCodeEnum.UserExport]) ? (
							<Button
								variant="contained"
								onClick={exportSmsRecipients}
								color="success"
								startIcon={isExportLoading && <CircularProgress size={'0.5rem'} />}
								disabled={isExportLoading}
							>
								{t('Export')}
							</Button>
						) : undefined} */}
					</>
				}
				haveGlobalSearch={true}
				criterion={[
					{
						key: 'name',
						name: t('Name'),
						type: 'StringFilter',
					},

					{
						key: 'phone',
						name: t('Phone'),
						type: 'StringFilter',
					},
					{
						key: 'buildings',
						name: t('SMS Group'),
						type: 'StringFilter',
					},
				]}
				defaultFilterCriteria={defaultFilterCriteria}
				defaultSorter={{ by: 'name', order_by: 'ASC' }}
				haveNoFilter
			/>

			{/* Delete Confirmation Dialog */}
			<Dialog
				open={!!recipientToDelete}
				onClose={() => setRecipientToDelete(null)}
			>
				<DialogTitle>{t('Delete SMS Recipient')}</DialogTitle>
				<DialogContent>
					<Typography>
						{t('Are you sure you want to delete')} <strong>{recipientToDelete?.name}</strong>?
					</Typography>
					<Typography sx={{ marginTop: '8px' }}>
						{t('This action cannot be undone.')}
					</Typography>
				</DialogContent>
				<DialogActions>
					<Button onClick={() => setRecipientToDelete(null)} color="primary">
						{t('Cancel')}
					</Button>
					<Button
						onClick={() => {
							if (recipientToDelete) {
								deleteSmsRecipient(recipientToDelete)
								setRecipientToDelete(null)
							}
						}}
						color="error"
						variant="contained"
						disabled={isLoading}
					>
						{t('Delete')}
					</Button>
				</DialogActions>
			</Dialog>
		</>
	)
}
